import { ButtonPrimary, ButtonTertiary } from "@nova/component-btn"
import { allowedFileExtensions } from "@nova/constants"
import { DropBoxV2 } from "panels/content/top-bar/TODO-put-in-package/dropbox/DropBoxV2"
import * as Icons from "@nova/component-icons"
import { AddMoreDropBox } from "panels/content/top-bar/TODO-put-in-package/dropbox/AddMoreDropBox"
import { ScrollableArea } from "@nova/component-scrollable-area"
import classNames from "classnames"
import { splitExtension } from "@nova/util-strings"
import { FirePerimeterFileMetadataEntry } from "plugins/DrawPerimeter/uploads/FirePerimeterFileMetadataEntry"
import {
    firePerimeterAddMoreFilesTitle,
    firePerimeterNoPolygonsErrorTitle,
    firePerimeterUploadTitle,
} from "plugins/DrawPerimeter/constants"
import { AlertContainer } from "@nova/component-alert-container"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { Polygon } from "@nova/protobuf"
import {
    addMultiplePolygonsToFirePerimeterPreview,
    selectEditingFirePerimeterId,
} from "plugins/DrawPerimeter/FirePerimeterSlice"
import { useEffectUnsafe } from "@nova/use-effect-unsafe"
import { selectFirePerimeterById } from "features/entity-caching/fire-perimeters/selectors"
import { submitPerimeterUpload } from "features/entity-caching/fire-perimeters/thunks"
import { useFirePerimeterMethodUploadFiles } from "plugins/DrawPerimeter/useFirePerimeterMethodUploadFiles"

export type FileMetadata = {
    file: File
    name: string
    length: string
    area: string
    size: string
    dateCreated: string
    hasLine: boolean
    polygons: Polygon[]
}
export const FirePerimeterPluginActionMethodUpload = ({ onBlur }: { onBlur: () => void }) => {
    const dispatch = useAppDispatch()

    const perimeterId = useAppSelector(selectEditingFirePerimeterId())
    const existingPerimeter = useAppSelector(selectFirePerimeterById(perimeterId || ""))

    const { files, hasProcessingError, deleteFileAtIndex, handleNewItems } =
        useFirePerimeterMethodUploadFiles()

    // Initally add all of the existing perimeter's polygons to the preview layer.
    useEffectUnsafe(() => {
        if (!existingPerimeter) return

        dispatch(
            addMultiplePolygonsToFirePerimeterPreview(
                existingPerimeter.polygons.map((polygon) => polygon.coordinates)
            )
        )
    }, [existingPerimeter?.polygons.length])

    return files.length > 0 ? (
        <div className="flex-1 flex flex-col overflow-y-hidden min-h-0 gap-2 -mr-3">
            <ScrollableArea shouldPreventScrollbarShifting={true} className="pr-3">
                {files.map((file, i) => {
                    const [fileName, extension] = splitExtension(file.name)

                    return (
                        <div
                            className={classNames(
                                "relative pt-2 border-t border-gray-700 select-none",
                                {
                                    "mt-2": i > 0,
                                }
                            )}
                            key={file.name + i}
                        >
                            <div className="flex justify-between items-center gap-1 h-5">
                                <span className="flex items-center overflow-hidden whitespace-nowrap">
                                    <h5 className="text-gray-0 overflow-hidden text-ellipsis whitespace-nowrap">
                                        {fileName}
                                    </h5>
                                    {extension && (
                                        <h5 className="text-gray-0 flex-shrink-0">.{extension}</h5>
                                    )}
                                </span>

                                <Icons.SmallIcon
                                    Icon={Icons.Trash}
                                    className="text-gray-300 hover:text-red-500 cursor-pointer"
                                    onClick={() => deleteFileAtIndex(i)}
                                />
                            </div>

                            <div className="flex flex-col gap-1 mt-2">
                                <FirePerimeterFileMetadataEntry
                                    title="Perimeter Length"
                                    value={file.length}
                                />
                                <FirePerimeterFileMetadataEntry
                                    title="Perimeter Area"
                                    value={file.area}
                                />
                                <FirePerimeterFileMetadataEntry
                                    title="File Size"
                                    value={file.size}
                                />
                                <FirePerimeterFileMetadataEntry
                                    title="Date Created"
                                    value={file.dateCreated}
                                />
                            </div>

                            {file.hasLine && (
                                <AlertContainer
                                    type="info"
                                    title="Line file selected."
                                    body="We'll automatically close it into a polygon."
                                    className="mt-2"
                                />
                            )}
                        </div>
                    )
                })}
            </ScrollableArea>

            <AddMoreDropBox
                canAcceptMultiple
                allowedFileExtensions={allowedFileExtensions["FirePerimeter"]}
                onNewItems={handleNewItems}
                isProcessing={false}
                className="mr-3"
                title={
                    !hasProcessingError
                        ? firePerimeterAddMoreFilesTitle
                        : firePerimeterNoPolygonsErrorTitle
                }
                hasProcessingError={hasProcessingError}
            />

            <ButtonPrimary
                title="Confirm"
                RightIcon={Icons.Checkmark}
                onClick={() => dispatch(submitPerimeterUpload())}
                className="mr-3"
            />
        </div>
    ) : (
        <div className="flex flex-col gap-2">
            <DropBoxV2
                canAcceptMultiple
                allowedFileExtensions={allowedFileExtensions["FirePerimeter"]}
                onNewItems={handleNewItems}
                isProcessing={false}
                className="w-full mt-2"
                horizontalPaddingInRem={0.75}
                title={
                    !hasProcessingError
                        ? firePerimeterUploadTitle
                        : firePerimeterNoPolygonsErrorTitle
                }
                hasProcessingError={hasProcessingError}
            />
            <ButtonTertiary title="Cancel" onClick={onBlur} />
        </div>
    )
}
